import { useState, useEffect } from "react";
import { FiDatabase, FiCheckCircle } from "react-icons/fi";
import talkdeskLogo from "./images/talkdesk.png";
import { useHubSpotPortals } from "../hooks/useHubSpotPortals";
import { talkdeskApi } from "../services/api";

export default function ExternalSystems() {
    const { selectedPortal } = useHubSpotPortals();
    const portalId = selectedPortal?.portal_id;

    const [accountName, setAccountName] = useState("");
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");

    const [connected, setConnected] = useState(false);
    const [connectedAccount, setConnectedAccount] = useState("");
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!portalId) return;
        async function checkStatus() {
            try {
                const data = await talkdeskApi.getStatus(portalId);
                if (data.connected) {
                    setConnected(true);
                    setConnectedAccount(data.account_name || "");
                }
            } catch {
                setConnected(false);
            } finally {
                setLoadingStatus(false);
            }
        }
        checkStatus();
    }, [portalId]);

    async function handleConnect(e) {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            await talkdeskApi.connect({
                portal_id: portalId,
                account_name: accountName.trim(),
                client_id: clientId.trim(),
                client_secret: clientSecret,
            });
            setConnected(true);
            setConnectedAccount(accountName.trim());
            setClientSecret("");
        } catch {
            setError("Failed to connect. Check your credentials and try again.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <div className="flex items-center mb-2">
                    <FiDatabase className="w-6 h-6 text-gray-600 mr-3" />
                    <h1 className="text-2xl font-bold text-gray-900">External Systems</h1>
                </div>
                <p className="text-gray-600">
                    Connect external systems to Distributely to use in distributions.
                </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
                <div className="grid grid-cols-2 gap-8 items-center">
                    <div className="flex items-start gap-3">
                        <img src={talkdeskLogo} alt="Talkdesk" className="w-10 h-10 flex-shrink-0" />
                        <div>
                            <span className="text-lg font-medium text-gray-900">Talkdesk</span>
                            <p className="text-xs text-gray-400 mt-0.5">Required scopes: users:read, ccaas-user-status:read</p>
                        </div>
                    </div>

                    <div>
                        {loadingStatus ? (
                            <p className="text-sm text-gray-400">Checking connection…</p>
                        ) : connected ? (
                            <div className="flex items-center gap-2 text-green-600">
                                <FiCheckCircle className="w-5 h-5" />
                                <span className="text-sm font-medium">
                                    Connected{connectedAccount ? ` · ${connectedAccount}` : ""}
                                </span>
                            </div>
                        ) : (
                            <form onSubmit={handleConnect} className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Account Name
                                    </label>
                                    <input
                                        type="text"
                                        value={accountName}
                                        onChange={(e) => setAccountName(e.target.value)}
                                        placeholder="mycompany"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                        autoComplete="off"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Client ID
                                    </label>
                                    <input
                                        type="text"
                                        value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        placeholder="Client ID"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                        autoComplete="off"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Client Secret
                                    </label>
                                    <input
                                        type="password"
                                        value={clientSecret}
                                        onChange={(e) => setClientSecret(e.target.value)}
                                        placeholder="Client Secret"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                        autoComplete="off"
                                    />
                                </div>
                                {error && (
                                    <p className="text-sm text-red-600">{error}</p>
                                )}
                                <button
                                    type="submit"
                                    disabled={saving || !accountName.trim() || !clientId.trim() || !clientSecret}
                                    className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? "Connecting…" : "Connect"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

}
